import React, { createContext, ReactNode, useContext, useState } from "react";
import { filter, keyBy, mapValues, merge } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Bounds } from "viewport-mercator-project";
import {
  defaultsForFirmGroupComponents,
  generateDefaultLayerState,
  LayerState as CommonLayerState,
  MultipleSelectionGroup,
  WarningGroup,
} from "common-client/utils/layerState";
import {
  ApproximateBfeToolReducer,
  useApproximateBfeTool,
} from "common-client/utils/useApproximateBfeTool";
import {
  MeasureToolReducer,
  useMeasureTool,
} from "common-client/utils/useMeasureTool";
import {
  CustomMap,
  CustomObjectGroup,
  MapsForAccountQuery,
  PropertyWarningGroup,
} from "../../../generated/graphql";
import { AuthContext } from "../../Authorization/AuthContext";
import { Point } from "../LayeredMap/types";

type OptionalLayers =
  | "firms"
  | "rasters"
  | "baseMaps"
  | "accountDocumentTypes"
  | "accountPropertyWarningDefinitions";
export type Account = Omit<
  NonNullable<MapsForAccountQuery["account"]>,
  OptionalLayers | "name"
> &
  Partial<Pick<NonNullable<MapsForAccountQuery["account"]>, OptionalLayers>>;

export type FIRM = NonNullable<MapsForAccountQuery["account"]>["firms"][number];
export type Raster = NonNullable<
  MapsForAccountQuery["account"]
>["rasters"][number];
export type SavedView = NonNullable<
  NonNullable<MapsForAccountQuery["account"]>["savedViews"]
>[number];

export type BaseMap = NonNullable<Account["baseMaps"]>[number];

export type LayerState = CommonLayerState & {
  deviceLocations: { allDevices: boolean };
};
type LayerId = string;

export type Group = keyof LayerState;
export type GroupId<G extends Group> = KeysOfUnion<LayerState[G]>;

export type LayerContextType = {
  toggleLayer: <G extends Group>(args: {
    group: G;
    id?: GroupId<G>;
    isVisible: boolean;
  }) => void;
  isLayerVisible: <G extends Group>(args: {
    group: G;
    id: GroupId<G>;
  }) => boolean;
  isLayerGroupVisible: <G extends Group>(args: {
    group: G;
    id?: GroupId<G>;
  }) => boolean;
  visibleLayerIdsForGroup: (group: keyof LayerState) => LayerId[];
  visibleFIRM: () => FIRM | undefined;
  visibleRaster: () => Raster | undefined;
  visibleSavedViews: () => Array<SavedView> | undefined;
  rasters: Array<Raster>;
  mapNonce: string;
  updateMap: () => void;
  measureToolState: ReturnType<typeof useMeasureTool>[0];
  measureToolDispatch: ReturnType<typeof useMeasureTool>[1];
  approximateBfeToolState?: ReturnType<typeof useApproximateBfeTool>[0];
  approximateBfeToolDispatch?: ReturnType<typeof useApproximateBfeTool>[1];
  a11yEnabled: boolean;
  setA11yEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  floodTexturesEnabled: boolean;
  setFloodTexturesEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  geometryBounds: Maybe<Bounds>;
  setGeometryBounds: React.Dispatch<React.SetStateAction<Maybe<Bounds>>>;
  pointInGeometry: Maybe<Point>;
  setPointInGeometry: React.Dispatch<React.SetStateAction<Maybe<Point>>>;
  isSearching: boolean;
  setIsSearching: React.Dispatch<React.SetStateAction<boolean>>;
};

export const LayerContext = createContext<LayerContextType>({} as any);
export const LayerContextProvider = ({
  account,
  children,
  defaultState = {},
  noLayerPanel = false,
}: {
  account: Account;
  children: ReactNode;
  defaultState?: Partial<LayerState>;
  noLayerPanel?: boolean;
}) => {
  const { account: authAccount } = useContext(AuthContext);
  const firms = account.firms ?? [];
  const rasters = account.rasters ?? [];
  const savedViews = account.savedViews ?? [];
  // we don't need custom object groups if we're not showing the layer panel
  const customObjectGroups =
    noLayerPanel ? [] : (authAccount?.customObjectGroups ?? []);
  // we don't need custom maps if we're not showing the layer panel
  const customMaps = noLayerPanel ? [] : (authAccount?.customMaps ?? []);

  const defaultLayerState = generateDefaultLayerState({
    account,
    customObjectGroups,
    customMaps,
    defaultState,
    noLayerPanel,
  });

  const [layers, updateLayers] = useState<LayerState>({
    deviceLocations: { allDevices: false },
    ...defaultLayerState,
  });

  const customMapToCustomObjectGroup = customObjectGroups.reduce(
    (acc, group) => {
      group.customMapIds.forEach(customMapId => {
        acc[customMapId] ||= [];
        acc[customMapId].push(group.id);
      });
      return acc;
    },
    {} as Record<CustomMap["id"], Array<CustomObjectGroup["id"]>>,
  );

  const groupsById = keyBy(customObjectGroups, "id");

  const toggleLayer = <G extends Group>({
    group,
    id,
    isVisible,
  }: {
    group: G[] | G;
    id?: GroupId<G>;
    isVisible: boolean;
  }) => {
    let updatedLayers = merge({}, layers);
    let groups = Array.isArray(group) ? group : [group];

    for (const group of groups) {
      const warningGroups = Object.values(PropertyWarningGroup).map(
        group => `${group}Warnings` as WarningGroup,
      );
      // these groups allow for multiple toggles within the same group
      const allowMultipleSelectionGroups: Group[] = [
        ...warningGroups,
        "customMaps",
        "savedViews",
        "customObjectGroups",
      ];

      // if the group does not allow for multiple selections disable toggles from the same group
      if (!allowMultipleSelectionGroups.includes(group)) {
        updatedLayers[group as WarningGroup] = mapValues(
          layers[group as WarningGroup],
          () => false,
        );
      }
      // Some sections have mutually exclusive toggles,
      // so we disable toggles from the mutually exclusive groups before enabling just the one
      const mutuallyExclusiveGroups: Array<Array<Group>> = [
        ["firms", "rasters"],
        ["rasters", "customMaps"],
        ["documents", ...warningGroups, "savedViews"],
        ["buildings", "savedViews"],
      ];

      for (const mutuallyExclusiveGroup of mutuallyExclusiveGroups) {
        if (mutuallyExclusiveGroup.includes(group)) {
          for (const meg of mutuallyExclusiveGroup) {
            if (group !== meg) {
              updatedLayers[meg as WarningGroup] = mapValues(
                layers[meg as WarningGroup],
                () => false,
              );
            }
          }
        }
      }

      if (id) {
        if (updatedLayers[group]) {
          (updatedLayers[group] as any)[id] = isVisible;
        }

        if (group === "firms") {
          updatedLayers = {
            ...updatedLayers,
            ...defaultsForFirmGroupComponents(
              firms.map(firm => ({
                ...firm,
                visible: firm.id === id && isVisible,
              })),
            ),
          };
        }
        // if the group is a custom object group, we also need to update the relevant custom maps
        if (group === "customObjectGroups") {
          updatedLayers["customMaps"] = mapValues(
            layers["customMaps"],
            (previousValue, customMapId) =>
              groupsById[id]?.customMapIds.includes(customMapId) ?
                isVisible
              : previousValue,
          );
        }
        // if the group is a custom map, we also need to update the relevant custom object groups
        if (group === "customMaps") {
          customMapToCustomObjectGroup[id]?.forEach(groupId => {
            updatedLayers["customObjectGroups"][groupId] = isLayerGroupVisible({
              group: "customObjectGroups",
              id: groupId,
            });
          });
        }
      } else {
        updatedLayers[group as MultipleSelectionGroup] = mapValues(
          layers[group as MultipleSelectionGroup],
          () => isVisible,
        );
      }
    }
    updateLayers(updatedLayers);
  };

  const visibleLayerIdsForGroup = (group: Group) => {
    const groupLayers = layers[group];
    if (!groupLayers) return [];

    const enabledLayer = filter(
      Object.entries(groupLayers),
      ([_layer, enabled]) => enabled,
    );

    return enabledLayer.map(layer => layer[0]);
  };

  const visibleFIRM = () => {
    const firmId = visibleLayerIdsForGroup("firms")[0];
    return firms?.find(firm => firm.id === firmId);
  };

  const visibleRaster = () => {
    const rasterId = visibleLayerIdsForGroup("rasters")[0];
    return rasters?.find(raster => raster.id === rasterId);
  };

  const visibleSavedViews = () => {
    const viewIds = visibleLayerIdsForGroup("savedViews");
    return savedViews?.filter(view => viewIds.includes(view.id));
  };

  const isLayerVisible = <G extends Group>({
    group,
    id,
  }: {
    group: G;
    id: GroupId<G>;
  }) => {
    const groupLayers = layers[group];
    if (!groupLayers) return false;
    return !!(groupLayers as any)[id];
  };

  const isLayerGroupVisible = <G extends Group>({
    group,
    id,
  }: {
    group: G;
    id?: GroupId<G>;
  }) => {
    // this is a special case for custom object groups which is a synthetic group
    // that is composed of the custom maps in the group
    if (group === "customObjectGroups" && id) {
      return (
        groupsById[id]?.customMapIds.every(
          customMapId => layers["customMaps"][customMapId],
        ) ?? false
      );
    }
    const currentLayers = layers[group as WarningGroup];
    return Object.values(currentLayers).every(layer => layer);
  };

  const [mapNonce, setMapNonce] = useState(uuidv4());
  const updateMap = () => setMapNonce(uuidv4());

  const [measureToolState, measureToolDispatch] = useMeasureTool({
    defaultValue: {
      measureToolMode: "off",
      coordinates: [],
      loading: false,
    },
    useReducer: React.useReducer as MeasureToolReducer,
  });

  const [approximateBfeToolState, approximateBfeToolDispatch] =
    useApproximateBfeTool({
      defaultValue: {
        mode: "off",
        crossSections: [],
        disableMapInteractions: false,
      },
      useReducer: React.useReducer as ApproximateBfeToolReducer,
      showBfeLayers: () => {
        const id = visibleFIRM()?.id;
        toggleLayer({
          group: ["profileBaselines", "crossSections"],
          id,
          isVisible: true,
        });
      },
    });

  const [a11yEnabled, setA11yEnabled] = useState(false);
  const [floodTexturesEnabled, setFloodTexturesEnabled] = useState(false);

  const [geometryBounds, setGeometryBounds] = useState<Maybe<Bounds>>(null);
  const [pointInGeometry, setPointInGeometry] = useState<Maybe<Point>>(null);

  const [isSearching, setIsSearching] = useState(false);

  const value = {
    toggleLayer,
    isLayerVisible,
    isLayerGroupVisible,
    visibleFIRM,
    visibleRaster,
    visibleSavedViews,
    visibleLayerIdsForGroup,
    mapNonce,
    updateMap,
    measureToolDispatch,
    measureToolState,
    approximateBfeToolState,
    approximateBfeToolDispatch,
    a11yEnabled,
    setA11yEnabled,
    geometryBounds,
    setGeometryBounds,
    pointInGeometry,
    setPointInGeometry,
    floodTexturesEnabled,
    setFloodTexturesEnabled,
    rasters,
    isSearching,
    setIsSearching,
  };

  return (
    <LayerContext.Provider value={value}>{children}</LayerContext.Provider>
  );
};
